import {
  GridFilterModel,
  GridSortModel,
  GridColumnVisibilityModel,
  GridPinnedColumns,
  GridApiCommunity,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-premium';
import { BaseView, GridStateUtils } from './types';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';

/**
 * Creates grid state utility functions for a given API reference
 */
export const createGridStateUtils = (
  apiRef: React.MutableRefObject<GridApiCommunity>,
  isGroupingEnabled?: boolean
): GridStateUtils => {
  // Grid state getters
  const getColumnFilters = (): GridFilterModel => {
    if (apiRef.current && apiRef.current.state && apiRef.current.state.filter) {
      return apiRef.current.state.filter.filterModel || { items: [] };
    }
    return { items: [] };
  };

  const getSortModel = (): GridSortModel => {
    if (
      apiRef.current &&
      apiRef.current.state &&
      apiRef.current.state.sorting
    ) {
      return apiRef.current.state.sorting.sortModel || [];
    }
    return [];
  };

  const getColumnVisibility = (): GridColumnVisibilityModel => {
    if (
      apiRef.current &&
      apiRef.current.state &&
      apiRef.current.state.columns &&
      apiRef.current.state.columns.columnVisibilityModel
    ) {
      return apiRef.current.state.columns.columnVisibilityModel;
    }
    return {};
  };

  const getPinnedColumns = (): GridPinnedColumns => {
    if (apiRef.current) {
      const pinnedColumns =
        apiRef.current.getPinnedColumns?.() ||
        apiRef.current.state?.columns?.pinnedColumns;
      if (pinnedColumns) {
        return {
          left: [...(pinnedColumns.left || [])],
          right: [...(pinnedColumns.right || [])],
        };
      }
    }
    return { left: [], right: [] };
  };

  // Grid state setters
  const applyColumnFilters = (filters: GridFilterModel): void => {
    if (apiRef.current && filters) {
      apiRef.current.setFilterModel(filters);
    }
  };

  const applySortModel = (sortModel: GridSortModel): void => {
    if (apiRef.current && sortModel) {
      apiRef.current.setSortModel(sortModel);
    }
  };

  const applyColumnVisibility = (
    visibilityModel: GridColumnVisibilityModel
  ): void => {
    if (apiRef.current) {
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  };

  const applyPinnedColumns = (pinnedColumns: GridPinnedColumns): void => {
    if (!apiRef.current) return;
    try {
      let finalPinnedColumns = pinnedColumns;

      // Always ensure checkbox column is first, then add group columns if grouping is enabled
      const protectedColumns = [GRID_CHECKBOX_SELECTION_COL_DEF.field];

      if (isGroupingEnabled) {
        protectedColumns.push(GRID_TREE_DATA_GROUPING_FIELD, 'group_name');
      }

      // Get user-defined columns by filtering out all protected columns
      const userLeftColumns = (pinnedColumns.left || []).filter(
        col => !protectedColumns.includes(col)
      );

      // Always rebuild left columns with protected columns first, then user columns
      finalPinnedColumns = {
        ...pinnedColumns,
        left: [...protectedColumns, ...userLeftColumns],
      };

      if (typeof apiRef.current.setPinnedColumns === 'function') {
        apiRef.current.setPinnedColumns(finalPinnedColumns);
      } else if (apiRef.current.setColumnPinning) {
        apiRef.current.setColumnPinning(finalPinnedColumns);
      }
    } catch (error) {
      console.error('Error applying pinned columns:', error);
    }
  };

  return {
    getColumnFilters,
    getSortModel,
    getColumnVisibility,
    getPinnedColumns,
    applyColumnFilters,
    applySortModel,
    applyColumnVisibility,
    applyPinnedColumns,
  };
};

/**
 * Creates a new view with current grid state
 */
export const createViewFromCurrentState = <TView extends BaseView>(
  baseView: Omit<
    TView,
    'columnFilters' | 'sortModel' | 'columnVisibility' | 'pinnedColumns'
  >,
  gridUtils: GridStateUtils
): TView => {
  // Get current pinned columns and filter out ALL protected columns (not just grouping-related)
  const currentPinnedColumns = gridUtils.getPinnedColumns();

  // Define all protected columns that should not be saved in views
  const protectedColumns = [
    GRID_CHECKBOX_SELECTION_COL_DEF.field,
    GRID_TREE_DATA_GROUPING_FIELD,
    'group_name',
  ];

  // Filter out protected columns from both left and right pinned columns
  const filteredPinnedColumns = {
    left: (currentPinnedColumns.left || []).filter(
      col => !protectedColumns.includes(col)
    ),
    right: (currentPinnedColumns.right || []).filter(
      col => !protectedColumns.includes(col)
    ),
  };

  return {
    ...baseView,
    columnFilters: gridUtils.getColumnFilters(),
    sortModel: gridUtils.getSortModel(),
    columnVisibility: gridUtils.getColumnVisibility(),
    pinnedColumns: filteredPinnedColumns,
  } as TView;
};

/**
 * Applies a view to the grid with proper error handling
 */
export const applyViewToGrid = async <
  TView extends BaseView,
  TRowId = string | number,
>(
  view: TView,
  gridUtils: GridStateUtils,
  searchHandler: (term: string) => void,
  setSelectedRows: (rows: TRowId[]) => void,
  setAutosizeColumns: (autosize: boolean) => void,
  setIsGroupingEnabled?: (enabled: boolean) => void
): Promise<void> => {
  try {
    // Apply grouping enabled state FIRST (for views that support grouping)
    // This must happen before any grid state is applied to ensure the grid
    // is in the correct grouping mode for proper column configuration
    if (setIsGroupingEnabled && 'isGroupingEnabled' in view) {
      setIsGroupingEnabled(view.isGroupingEnabled || false);
    }

    // Wait a short delay to allow the grouping state to propagate and the grid to reconfigure
    if (view.isGroupingEnabled) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Apply search
    searchHandler(view.searchTerm);

    // Apply autosize columns
    setAutosizeColumns(view.autosizeColumns || false);

    // Apply filters
    if (view.columnFilters) {
      gridUtils.applyColumnFilters(view.columnFilters);
    }

    // Apply sort
    if (view.sortModel) {
      gridUtils.applySortModel(view.sortModel);
    }

    // Apply column visibility
    if (view.columnVisibility) {
      gridUtils.applyColumnVisibility(view.columnVisibility);
    }

    // Apply pinned columns with requestAnimationFrame for proper timing
    if (view.pinnedColumns) {
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          gridUtils.applyPinnedColumns(view.pinnedColumns!);
          resolve();
        });
      });
    }

    // Apply selected rows
    if (view.selectedRows && view.selectedRows.length > 0) {
      setSelectedRows(view.selectedRows);
    } else {
      setSelectedRows([]);
    }

    showSnackbar({
      title: 'View Applied',
      type: SnackbarType.info,
      message: `Applied view: ${view.name}`,
    });
  } catch (error) {
    console.error('Error applying view:', error);
    showSnackbar({
      title: 'Error',
      type: SnackbarType.error,
      message: 'Failed to apply view',
    });
    throw error;
  }
};

/**
 * Compares current grid state with an applied view to detect modifications
 */
export const compareViewWithCurrentState = <
  TView extends BaseView,
  TRowId = string | number,
>(
  activeView: TView | null,
  currentState: {
    searchTerm: string;
    selectedRows: TRowId[];
    autosizeColumns: boolean;
    isGroupingEnabled?: boolean;
    displayData?: Record<string, unknown>[];
  },
  gridUtils: GridStateUtils,
  options: {
    excludeRowSelection?: boolean;
  } = {}
): string[] => {
  if (!activeView) return [];

  const modifications: string[] = [];

  // Compare search term
  if (currentState.searchTerm !== activeView.searchTerm) {
    modifications.push(`Search: "${currentState.searchTerm || '(none)'}"`);
  }

  // Compare selected rows - with smart handling for filtered views (unless excluded)
  if (!options.excludeRowSelection) {
    if (activeView.selectedRows && activeView.selectedRows.length > 0) {
      const viewRowIds = new Set(activeView.selectedRows);
      const currentRowIds = new Set(currentState.selectedRows);

      // Check if current selection matches the view's saved rows exactly
      const selectionsMatch =
        viewRowIds.size === currentRowIds.size &&
        [...viewRowIds].every(id => currentRowIds.has(id));

      // Handle display data filtering if available
      if (currentState.displayData && !selectionsMatch) {
        const displayedRowIds = new Set(
          currentState.displayData.map(
            (row: Record<string, unknown>) => row.id || row._id
          )
        );
        const allViewRowsDisplayed = [...viewRowIds].every(id =>
          displayedRowIds.has(id)
        );
        const currentMatchesDisplayed =
          currentRowIds.size === displayedRowIds.size &&
          [...currentRowIds].every(id => displayedRowIds.has(id));

        const isClean =
          selectionsMatch ||
          (allViewRowsDisplayed && currentMatchesDisplayed) ||
          (allViewRowsDisplayed && currentRowIds.size === 0);

        if (!isClean) {
          modifications.push(
            `Selection: ${currentState.selectedRows.length} row(s) selected`
          );
        }
      } else if (!selectionsMatch) {
        modifications.push(
          `Selection: ${currentState.selectedRows.length} row(s) selected`
        );
      }
    } else if (currentState.selectedRows.length > 0) {
      modifications.push(
        `Selection: ${currentState.selectedRows.length} row(s) selected`
      );
    }
  }

  // Compare filters
  try {
    const currentFilters = gridUtils.getColumnFilters();
    const viewFilters = activeView.columnFilters || { items: [] };
    const currentFilterCount = currentFilters.items?.length || 0;
    const viewFilterCount = viewFilters.items?.length || 0;

    // Only show modification if there's a meaningful change in filter count
    if (
      currentFilterCount !== viewFilterCount ||
      (currentFilterCount > 0 &&
        JSON.stringify(currentFilters) !== JSON.stringify(viewFilters))
    ) {
      modifications.push(`Filters: ${currentFilterCount} filter(s)`);
    }
  } catch (error) {
    console.warn('Error comparing filters:', error);
  }

  // Compare autosize columns
  if (currentState.autosizeColumns !== (activeView.autosizeColumns || false)) {
    modifications.push(
      `Autosize columns: ${currentState.autosizeColumns ? 'Yes' : 'No'}`
    );
  }

  // Compare grouping enabled state (only for views that support grouping)
  if (currentState.isGroupingEnabled !== undefined) {
    const viewGroupingState = activeView.isGroupingEnabled || false;
    if (currentState.isGroupingEnabled !== viewGroupingState) {
      modifications.push(
        `Groups: ${currentState.isGroupingEnabled ? 'Enabled' : 'Disabled'}`
      );
    }
  }

  // Compare sort
  try {
    const currentSort = gridUtils.getSortModel();
    const viewSort = activeView.sortModel || [];
    if (JSON.stringify(currentSort) !== JSON.stringify(viewSort)) {
      modifications.push(`Sort: ${currentSort.length} column(s) sorted`);
    }
  } catch (error) {
    console.warn('Error comparing sort:', error);
  }

  // Compare column visibility
  try {
    const currentVisibility = gridUtils.getColumnVisibility();
    const viewVisibility = activeView.columnVisibility || {};

    // Count hidden columns in current state
    const currentHiddenCount = Object.values(currentVisibility).filter(
      v => v === false
    ).length;
    const viewHiddenCount = Object.values(viewVisibility).filter(
      v => v === false
    ).length;

    // Compare visibility models
    if (JSON.stringify(currentVisibility) !== JSON.stringify(viewVisibility)) {
      modifications.push(
        `Column visibility: ${currentHiddenCount} column(s) hidden`
      );
    }
  } catch (error) {
    console.warn('Error comparing column visibility:', error);
  }

  // Compare pinned columns (excluding grouping-related columns from both sides)
  try {
    const currentPinnedColumns = gridUtils.getPinnedColumns();
    const viewPinnedColumns = activeView.pinnedColumns || {
      left: [],
      right: [],
    };

    // Define ALL protected columns that should be excluded from user pinning comparison
    // These are system-managed columns that should not count as "user modifications"
    const protectedColumns = [
      GRID_CHECKBOX_SELECTION_COL_DEF.field,
      GRID_TREE_DATA_GROUPING_FIELD,
      'group_name',
    ];

    // Filter out protected columns from BOTH current and saved view to get only user-pinned columns
    // This ensures that protected columns don't affect the "modified" status comparison
    const currentUserLeft = (currentPinnedColumns.left || []).filter(
      col => !protectedColumns.includes(col)
    );
    const viewUserLeft = (viewPinnedColumns.left || []).filter(
      col => !protectedColumns.includes(col)
    );
    const currentUserRight = currentPinnedColumns.right || [];
    const viewUserRight = viewPinnedColumns.right || [];

    const currentUserPinned = {
      left: currentUserLeft,
      right: currentUserRight,
    };
    const viewUserPinned = { left: viewUserLeft, right: viewUserRight };

    if (JSON.stringify(currentUserPinned) !== JSON.stringify(viewUserPinned)) {
      const userLeftCount = currentUserLeft.length;
      const userRightCount = currentUserRight.length;
      const totalUserCount = userLeftCount + userRightCount;
      modifications.push(`Pinned columns: ${totalUserCount} column(s) pinned`);
    }
  } catch (error) {
    console.warn('Error comparing pinned columns:', error);
  }

  return modifications;
};

/**
 * Resets grid to default state
 */
export const resetGridToDefault = (
  gridUtils: GridStateUtils,
  apiRef: React.MutableRefObject<GridApiCommunity>,
  searchHandler: (term: string) => void,
  setSelectedRows: (rows: (string | number)[]) => void,
  setAutosizeColumns: (autosize: boolean) => void,
  loadColumnVisibilityPreferences?: () => Promise<void>,
  setIsGroupingEnabled?: (enabled: boolean) => void
): void => {
  // Reset search
  searchHandler('');

  // Reset selections
  setSelectedRows([]);

  // Reset autosize
  setAutosizeColumns(false);

  // Reset groups state
  if (setIsGroupingEnabled) {
    setIsGroupingEnabled(false);
  }

  // Reset grid state
  if (apiRef.current) {
    // Clear filters
    apiRef.current.setFilterModel({ items: [] });

    // Clear sort
    apiRef.current.setSortModel([]);

    // Reset column visibility
    if (loadColumnVisibilityPreferences) {
      loadColumnVisibilityPreferences();
    } else {
      apiRef.current.setColumnVisibilityModel({});
    }

    // Reset pinned columns
    try {
      if (typeof apiRef.current.setPinnedColumns === 'function') {
        apiRef.current.setPinnedColumns({ left: ['__check__'], right: [] });
      } else if (apiRef.current.setColumnPinning) {
        apiRef.current.setColumnPinning({ left: ['__check__'], right: [] });
      }
    } catch (error) {
      console.error('Error resetting pinned columns:', error);
    }
  }
};

/**
 * Validates a view object
 */
export const validateView = <TView extends BaseView>(view: TView): boolean => {
  if (!view || typeof view !== 'object') return false;
  if (!view.id || typeof view.id !== 'string') return false;
  if (!view.name || typeof view.name !== 'string') return false;
  if (typeof view.searchTerm !== 'string') return false;

  return true;
};

/**
 * Sanitizes a view object by removing invalid properties
 */
export const sanitizeView = <TView extends BaseView>(view: TView): TView => {
  const sanitized: Partial<TView> = {
    id: view.id,
    name: view.name,
    searchTerm: view.searchTerm || '',
    autosizeColumns: Boolean(view.autosizeColumns),
  };

  if (Array.isArray(view.selectedRows)) {
    sanitized.selectedRows = view.selectedRows;
  }

  if (view.columnFilters && typeof view.columnFilters === 'object') {
    sanitized.columnFilters = view.columnFilters;
  }

  if (Array.isArray(view.sortModel)) {
    sanitized.sortModel = view.sortModel;
  }

  if (view.columnVisibility && typeof view.columnVisibility === 'object') {
    sanitized.columnVisibility = view.columnVisibility;
  }

  if (view.pinnedColumns && typeof view.pinnedColumns === 'object') {
    sanitized.pinnedColumns = view.pinnedColumns;
  }

  return sanitized as TView;
};

/**
 * Generates a unique view ID
 */
export const generateViewId = (): string => {
  return `view-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Deep clones a view object
 */
export const cloneView = <TView extends BaseView>(view: TView): TView => {
  return JSON.parse(JSON.stringify(view));
};

/**
 * Filters data based on selected rows in the active view
 * @param data - The data array to filter
 * @param activeView - The currently active view
 * @param idField - The field name to use for row IDs ('id' or '_id')
 * @returns Filtered data array showing only selected rows when a view is active
 */
export const filterDataBySelectedRows = <T extends Record<string, unknown>>(
  data: T[],
  activeView: BaseView | null,
  idField: 'id' | '_id' = 'id'
): T[] => {
  if (activeView?.selectedRows && activeView.selectedRows.length > 0) {
    return data.filter(row => {
      const rowId = row[idField];
      // Handle both string and number comparisons
      return activeView.selectedRows!.some(selectedId => {
        // Compare as strings
        if (selectedId?.toString() === rowId?.toString()) return true;
        // Compare as numbers (if both can be converted to numbers)
        const numericSelected = Number(selectedId);
        const numericRow = Number(rowId);
        if (!isNaN(numericSelected) && !isNaN(numericRow)) {
          return numericSelected === numericRow;
        }
        return false;
      });
    });
  }
  return data;
};
